import { JobOpeningProps } from "@/lib/types";

export const jobOpeningsData: JobOpeningProps[] = [
  {
    id: "J-001",
    title: "Mid-Level React Native Developer",
    experienceLevel: "Mid",
    location: "Remote",
    isRemote: true,
    type: "Contractual",
    salaryRange: "Negotiable",
    postedAt: "2026-03-01",
    deadline: "2026-05-01",
    description:
      "This is a remote, contractual role (1–2 months) focused on delivering a production-ready mobile app. You’ll be responsible for architecture, performance optimization, and preparing builds for both the Google Play Store and Apple App Store.",
    responsibilities: [
      "Design and implement robust React Native architecture for high-performance apps.",
      "Publish and maintain apps on both Google Play Store and Apple App Store.",
      "Optimize applications for maximum speed, smooth user experience, and reliability.",
      "Build streaming applications or real-time video/audio calling features.",
      "Work with REST APIs, WebSockets, and real-time data handling.",
      "Integrate third-party SDKs, payment gateways, and other services.",
      "Mentor junior developers and conduct high-standard code reviews.",
    ],
    skills: [
      "Strong React Native architecture and performance optimization experience",
      "Proven experience publishing apps to both Play Store and App Store",
      "Experience with state management tools (Redux, Zustand, Context API)",
      "Solid understanding of REST APIs, WebSockets, and real-time data handling",
      "Familiarity with mobile security best practices and data handling",
      "Ability to debug, profile, and optimize app performance for smooth, efficient UX",
      "Clean, scalable code practices with a production-ready engineering mindset",
    ],
    niceToHave: [
      "Experience with streaming applications or real-time communication features",
      "Familiarity with Node.js and backend integrations, building and connecting robust server-side systems",
      "Previous experience in high-impact, short-term app development projects",
    ],
    benefits: [
      "100% Remote Workflow",
      "Paid Annual Learning Stipend",
      "Performance-based Yearly Bonus",
      "Flexible Paid Time Off (PTO)",
    ],
    applyLink: "https://forms.gle/izJBvdwEy1sBKXgDA",
  },
  {
    id: "J-002",
    title: "Mid-Level Full Stack Developer",
    experienceLevel: "Mid",
    location: "Remote",
    isRemote: true,
    type: "Full-time",
    salaryRange: "Up to BDT 50,000",
    postedAt: "2026-03-24",
    deadline: "2026-05-01",
    description:
      "We’re looking for a Mid-Level Full Stack Developer who enjoys building real products, not just writing code. You’ll work on scalable web applications, backend systems, and AI-integrated features across internal products and client projects.",
    responsibilities: [
      "Develop and maintain scalable web applications and backend systems.",
      "Integrate AI-driven features (OpenAI APIs, LLM-based solutions) into products.",
      "Work across frontend and backend stacks (React / Next.js, Node.js / NestJS).",
      "Design and implement REST APIs and modern backend architectures.",
      "Collaborate with team members on system design, code reviews, and deployments.",
      "Maintain clean, production-ready code and follow best practices.",
    ],
    skills: [
      "2+ years of Hands-on experience building scalable web applications",
      "Strong experience with Node.js and NestJS and ability to architect robust server-side systems",
      "Solid understanding of frontend frameworks (React / Next.js)",
      "Experience with REST APIs and modern backend architecture",
      "Familiarity with AI integrations (OpenAI APIs, LLM-based features, etc.)",
      "Skilled at designing end-to-end system architectures that are maintainable, scalable, and performant",
      "Experience working with databases (PostgreSQL / MongoDB)",
      "Knowledge of Git, version control, and collaborative workflows",
      "Ability to write clean, maintainable, and production-ready code",
    ],
    niceToHave: [
      "Experience building AI-powered applications, integrating LLMs or AI features into real products",
      "Basic DevOps knowledge (Docker, deployment, CI/CD), familiar with automated workflows",
      "Experience with cloud platforms (AWS / Vercel / GCP), deploying and managing scalable apps",
    ],
    benefits: [
      "100% Remote Workflow",
      "Paid Annual Learning Stipend",
      "Performance-based Yearly Bonus",
      "Flexible Paid Time Off (PTO)",
    ],
    applyLink: "https://forms.gle/rBgacEwiHa8wGFXZ8",
  },
  {
    id: "J-003",
    title: "Marketing & Sales Executive",
    experienceLevel: "Junior",
    location: "Remote",
    isRemote: true,
    type: "Full-time",
    salaryRange: "BDT 12,000 – 15,000/month + commission on confirmed deals",
    postedAt: "2026-08-23",
    deadline: "2026-09-15",
    description:
      "At Octarnal we build software, AI solutions, and digital products for businesses. Now we’re looking for someone who can take those solutions to the right people. This is a role for someone who thinks beyond simply “getting leads” — someone who can find opportunities, start conversations, understand what a business actually needs, and turn those conversations into meaningful relationships. Joining is immediate, and every confirmed deal earns a commission on top of the base salary. There is no application form for this role. If you really want it, figure out how to get our attention. Make us curious. Make us remember you. Make us want to talk to you. Maybe that’s your first marketing win at Octarnal.",
    responsibilities: [
      "Find and qualify B2B opportunities for our software, AI, and digital product services.",
      "Run cold outreach across email, LinkedIn, and calls, and start conversations that actually go somewhere.",
      "Research prospects, industries, and competitors before the first message goes out.",
      "Write outreach copy, proposals, and follow-ups that read human, not templated.",
      "Take discovery calls, understand the client’s real problem, and map it to what we build.",
      "Negotiate scope and pricing, then hand a confirmed deal over to the delivery team.",
      "Use AI tools to speed up research, list building, and first-draft outreach.",
      "Track the funnel and report what converts and what dies, so the next round of outreach is sharper.",
    ],
    skills: [
      "Strong understanding of B2B marketing and sales",
      "Good working knowledge of technology, software, SaaS, or AI products",
      "Strong English communication skills, written and spoken",
      "Lead generation and prospecting ability",
      "Experience or clear understanding of cold outreach and client acquisition",
      "Strong copywriting skills — able to write an opener people actually reply to",
      "Ability to research prospects, industries, and competitors independently",
      "Confidence communicating with potential clients, including decision makers",
      "Basic understanding of sales funnels and conversion",
      "Negotiation and relationship-building skills",
      "Proactive mindset with a willingness to experiment and learn",
    ],
    niceToHave: [
      "Hands-on use of AI tools (ChatGPT, Claude, or similar) for prospect research and outreach personalization",
      "Experience selling AI, automation, or software services to non-technical businesses",
      "Familiarity with CRM and outreach tooling (HubSpot, Apollo, Instantly, or similar)",
      "Experience working with international or agency-side clients",
    ],
    benefits: [
      "100% Remote Workflow",
      "Commission on every confirmed deal, on top of base salary",
      "Immediate joining — no waiting period",
      "Paid Annual Learning Stipend",
      "Flexible Paid Time Off (PTO)",
    ],
    applyLink:
      "mailto:hi@octarnal.com?subject=Marketing%20%26%20Sales%20Executive%20%E2%80%94%20Make%20Us%20Curious",
  },
];
